import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { File as FileIcon, Info } from 'lucide-react';
import { useSearchParams } from 'react-router';
import { clsx } from '../../../ds/clsx';
import { Select } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import {
  UploadAbortedError,
  portalDocumentHref,
  portalKeys,
  uploadPortalDocument,
  usePortalDocuments,
  usePortalOverview,
} from '../api';
import { PageError, PageSkeleton } from '../frame/PageState';
import frame from '../frame/Portal.module.css';
import { usePortal } from '../frame/PortalLayout';
import type { PortalDocumentFilter, PortalDocumentRow } from '../types';
import styles from './Documents.module.css';
import { UploadRow, type UploadItem } from './UploadRow';

const ACCEPT = 'application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png';

const FILTERS: PortalDocumentFilter[] = ['all', 'mine', 'shared', 'unit'];

function isFilter(value: string | null): value is PortalDocumentFilter {
  return value !== null && (FILTERS as string[]).includes(value);
}

function DocumentRow({ row }: { row: PortalDocumentRow }) {
  const href = portalDocumentHref(row.id);
  return (
    <div className={clsx(styles.row, row.canDownload && styles.rowLink)} role="row">
      <div className={styles.nameCell} role="cell">
        <FileIcon size={18} strokeWidth={1.5} aria-hidden="true" className={styles.fileIcon} />
        {row.canDownload ? (
          <a className={clsx(styles.name, styles.nameLink)} href={href} download>
            {row.name}
          </a>
        ) : (
          <span className={styles.name}>{row.name}</span>
        )}
      </div>
      <span className={styles.cell} role="cell">
        <span className={styles.mobileLabel}>Kategorie: </span>
        {row.category}
      </span>
      <span className={clsx(styles.cell, styles.sourceCell)} role="cell">
        {row.source === 'Freigegeben' ? <span className={styles.sharedDot} aria-hidden="true" /> : null}
        {row.source}
      </span>
      <span className={clsx(styles.cell, styles.tabular)} role="cell">
        {row.dateLabel}
      </span>
      <span className={styles.actionCell} role="cell">
        {row.canDownload ? (
          <a className={styles.action} href={href} download tabIndex={-1} aria-hidden="true">
            Herunterladen
          </a>
        ) : null}
      </span>
    </div>
  );
}

/**
 * `/portal/:orgSlug/documents` — Mandantenportal 18 (list with filter pills and the running upload) and 19 (empty):
 * the customer's own uploads and everything the organization released. „Dokument hochladen" opens the picker; with
 * open requests a small select „Für Anforderung" (own) chooses the request the file delivers (`?requestId=`).
 */
export function DocumentsPage() {
  const { me } = usePortal();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const filterParam = params.get('filter');
  const filter: PortalDocumentFilter = isFilter(filterParam) ? filterParam : 'all';
  const documents = usePortalDocuments(filter);
  const overview = usePortalOverview();
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const picker = useRef<HTMLInputElement>(null);
  const requests = useMemo(
    () => (overview.data?.phase === 'before_purchase' ? overview.data.todos : []),
    [overview.data],
  );
  const requestParam = params.get('requestId');
  const [requestId, setRequestId] = useState<string>('');

  useEffect(() => {
    if (requestParam && requests.some((request) => request.requestId === requestParam))
      setRequestId(requestParam);
  }, [requestParam, requests]);

  function setFilter(next: PortalDocumentFilter) {
    const copy = new URLSearchParams(params);
    if (next === 'all') copy.delete('filter');
    else copy.set('filter', next);
    setParams(copy, { replace: true });
  }

  function patch(key: string, change: Partial<UploadItem>) {
    setUploads((items) => items.map((item) => (item.key === key ? { ...item, ...change } : item)));
  }

  function start(file: File) {
    const key = `${Date.now()}-${file.name}`;
    const chosen = requests.find((request) => request.requestId === requestId) ?? null;
    const handle = uploadPortalDocument(file, chosen?.requestId ?? null, (fraction) =>
      patch(key, { progress: fraction }),
    );
    setUploads((items) => [
      {
        key,
        name: file.name,
        requestTitle: chosen?.title ?? null,
        progress: 0,
        error: null,
        abort: handle.abort,
      },
      ...items,
    ]);
    handle.promise
      .then(async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: portalKeys.documentsRoot }),
          queryClient.invalidateQueries({ queryKey: portalKeys.overview }),
        ]);
        setUploads((items) => items.filter((item) => item.key !== key));
        if (chosen) {
          setRequestId('');
          const copy = new URLSearchParams(params);
          copy.delete('requestId');
          setParams(copy, { replace: true });
        }
      })
      .catch((cause: unknown) => {
        if (cause instanceof UploadAbortedError)
          setUploads((items) => items.filter((item) => item.key !== key));
        else patch(key, { error: errorMessage(cause), abort: null });
      });
  }

  const uploadControls = (
    <div className={styles.uploadControls}>
      {requests.length > 0 ? (
        <Select
          label="Für Anforderung"
          size="compact"
          className={styles.requestSelect}
          value={requestId}
          onChange={(event) => setRequestId(event.target.value)}
          options={[
            { value: '', label: 'Keine bestimmte Anforderung' },
            ...requests.map((request) => ({ value: request.requestId, label: request.title })),
          ]}
        />
      ) : null}
      <button
        type="button"
        className={clsx('pressable', frame.primary)}
        onClick={() => picker.current?.click()}
      >
        Dokument hochladen
      </button>
    </div>
  );

  const picked = (
    <input
      ref={picker}
      type="file"
      accept={ACCEPT}
      className={styles.picker}
      tabIndex={-1}
      aria-hidden="true"
      onChange={(event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (file) start(file);
      }}
    />
  );

  if (documents.isPending) return <PageSkeleton blocks={[40, 360]} />;
  if (documents.isError) {
    return (
      <div className={frame.content}>
        <PageError
          what="Ihre Dokumente"
          error={documents.error}
          onRetry={() => void documents.refetch()}
          retrying={documents.isFetching}
        />
      </div>
    );
  }

  const data = documents.data;
  const empty = data.empty !== null && uploads.length === 0;
  const pills: { key: PortalDocumentFilter; label: string }[] = [
    { key: 'all', label: `Alle · ${data.counts.all}` },
    { key: 'mine', label: `Von mir · ${data.counts.mine}` },
    { key: 'shared', label: `Für mich freigegeben · ${data.counts.shared}` },
  ];
  if (data.unitChip) pills.push({ key: 'unit', label: data.unitChip.label });

  return (
    <div className={clsx(frame.content, styles.page)}>
      {picked}
      {empty ? <h1 className={styles.srOnly}>Dokumente</h1> : null}
      <div className={clsx(frame.pageHead, empty && styles.hidden)}>
        <div>
          <h1 className={frame.pageTitle}>Dokumente</h1>
          <div className={frame.pageText}>
            Alles, was Sie hochgeladen haben, und alles, was {me.organization.name} für Sie freigegeben hat.
          </div>
        </div>
        {empty ? null : uploadControls}
      </div>
      {empty && data.empty ? (
        <div className={clsx(frame.card, styles.empty)}>
          <FileIcon size={32} strokeWidth={1.5} aria-hidden="true" className={styles.emptyIcon} />
          <h2 className={styles.emptyTitle}>{data.empty.title}</h2>
          <p className={styles.emptyText}>{data.empty.text}</p>
          <button
            type="button"
            className={clsx('pressable', frame.primary, styles.emptyCta)}
            onClick={() => picker.current?.click()}
          >
            Dokument hochladen
          </button>
          {requests.length > 0 ? <div className={styles.emptySelect}>{uploadControls}</div> : null}
        </div>
      ) : (
        <>
          <div className={styles.pills} role="group" aria-label="Filter">
            {pills.map((pill) => (
              <button
                key={pill.key}
                type="button"
                className={clsx(styles.pill, filter === pill.key && styles.pillActive)}
                aria-pressed={filter === pill.key}
                onClick={() => setFilter(pill.key)}
              >
                {pill.label}
              </button>
            ))}
          </div>
          <div className={clsx(frame.card, styles.table)} role="table" aria-label="Dokumente">
            <div className={clsx(styles.row, styles.headRow)} role="row">
              <span role="columnheader">Dokument</span>
              <span role="columnheader">Kategorie</span>
              <span role="columnheader">Quelle</span>
              <span role="columnheader">Datum</span>
              <span role="columnheader">
                <span className={styles.srOnly}>Aktion</span>
              </span>
            </div>
            {uploads.map((item) => (
              <UploadRow
                key={item.key}
                item={item}
                onDismiss={(key) => setUploads((items) => items.filter((entry) => entry.key !== key))}
              />
            ))}
            {data.rows.map((row) => (
              <DocumentRow key={row.id} row={row} />
            ))}
            {data.rows.length === 0 && uploads.length === 0 ? (
              <div className={styles.noRows} role="row">
                <span role="cell">In dieser Ansicht liegen keine Dokumente.</span>
              </div>
            ) : null}
          </div>
          <div className={styles.footnote}>
            <Info size={14} strokeWidth={1.75} aria-hidden="true" />
            {data.footnote}
          </div>
        </>
      )}
    </div>
  );
}
